import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { BrandGroupSelect, Dropdown } from "components";
import { createFormHelper } from "lib/Form";
import { CHECKLIST_TYPES } from "models";
import { useChecklistTemplatesQuery, useTagsQuery } from "modules/ChecklistsDashboard/hooks";

import type { Checklist } from "models";

type EditChecklistFormProps = {
  canEdit: boolean;
};

const FormHelper = createFormHelper<Checklist>();

export const EditChecklistForm = ({ canEdit }: EditChecklistFormProps) => {
  const { t } = useTranslation();

  const dealerId = FormHelper.useWatch("dealer_id");
  const parentChecklist = FormHelper.useWatch("parent_checklist");
  const attachedTo = FormHelper.useWatch("attached_to")?.filter(attachedName => !!attachedName);
  const { data: checklistTemplates = [] } = useChecklistTemplatesQuery();
  const { data: tagsOptions = [] } = useTagsQuery();

  const canEditSettings = canEdit && !parentChecklist;

  const templatesOptions = checklistTemplates.flatMap(template =>
    dealerId && template.default_only ? [] : [{ key: template.checklist_template_id, value: template.checklist_template_id, text: template.name }]
  );

  const checklistTypesOptions = [
    { key: CHECKLIST_TYPES.Regular, value: CHECKLIST_TYPES.Regular, text: t("v8_regular", "Regular") },
    { key: CHECKLIST_TYPES.Aanname, value: CHECKLIST_TYPES.Aanname, text: t("v8_onboarding", "Onboarding") },
    { key: CHECKLIST_TYPES.Quality, value: CHECKLIST_TYPES.Quality, text: t("v8_quality", "Quality") },
    { key: CHECKLIST_TYPES.Info, value: CHECKLIST_TYPES.Info, text: t("v8_info", "Info") },
    { key: CHECKLIST_TYPES.ExtraParts, value: CHECKLIST_TYPES.ExtraParts, text: t("v8_extra_parts", "Extra parts") },
    { key: CHECKLIST_TYPES.TestDrive, value: CHECKLIST_TYPES.TestDrive, text: t("v8_test_drive", "Test Drive") },
    { key: CHECKLIST_TYPES.Warranty, value: CHECKLIST_TYPES.Warranty, text: t("v8_warranty", "Warranty") }
  ];

  return (
    <>
      <Form.Field>
        <label>{t("v8_checklist_name", "Checklist name")}</label>
        <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_checklist_name", "Checklist name"), fluid: true, disabled: !canEdit }} />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_checklist_template", "Checklist template")}</label>
        <FormHelper.Field.Dropdown
          name="template_id"
          options={templatesOptions}
          dropdownProps={{ placeholder: t("v8_checklist_template", "Checklist template"), fluid: true, selectOnBlur: false, disabled: !canEditSettings }}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_brand_groups", "Brand Groups")}</label>
        <FormHelper.Controller
          name="brand_group_ids"
          render={({ field }) => <BrandGroupSelect value={field.value ?? []} onChange={field.onChange} disabled={!canEditSettings} />}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_checklist_tags", "Checklist tags")}</label>
        <FormHelper.Field.Dropdown
          name="tag_ids"
          options={tagsOptions}
          dropdownProps={{ placeholder: t("v8_checklist_tags", "Checklist tags"), fluid: true, multiple: true, search: true, disabled: !canEditSettings }}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_checklist_settings", "Checklist settings")}</label>
        <FormHelper.Field.Dropdown
          name="checklist_type"
          options={checklistTypesOptions}
          dropdownProps={{ fluid: true, selectOnBlur: false, disabled: !canEditSettings }}
        />
      </Form.Field>

      {!dealerId && !!attachedTo?.length && (
        <Form.Field>
          <label>{t("v8_in_use_by", "In use by")}</label>
          <Dropdown
            multiple
            selection
            fluid
            disabled
            value={attachedTo}
            options={attachedTo.map(attachedName => ({ key: attachedName, value: attachedName, text: attachedName }))}
          />
        </Form.Field>
      )}

      <Form.Group inline className="checklist-toggles">
        <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} disabled={!canEditSettings} />
        <FormHelper.Field.Checkbox name="is_active_for_mechanic" label={t("v8_active_for_mechanic", "Active for mechanic")} disabled={!canEditSettings} />
        <FormHelper.Field.Checkbox name="include_in_reports" label={t("v8_mark_as_commercial", "Mark as commercial")} disabled={!canEditSettings} />
        <FormHelper.Field.Checkbox name="is_category_mode" label={t("v8_category_mode", "Category mode")} disabled={!canEditSettings} />
        <FormHelper.Field.Checkbox name="check_in_customer" label={t("v8_check_in_customer", "Check-in customer")} disabled={!canEditSettings} />
      </Form.Group>
    </>
  );
};
